/**
 * Pure line-item + VAT math for owner management invoices — the SINGLE source of
 * truth shared by the admin preview (src/lib/invoice.ts) and the emailed invoice
 * (supabase/functions/send-invoice). Keeping the math in one tested place stops
 * the preview / attached PDF / emailed document from disagreeing on a financial
 * document.
 *
 * MIRRORED at supabase/functions/_shared/management-invoice-lines.ts — keep the
 * two byte-for-byte in sync (same convention as src/lib/card-fees.ts). Edge
 * functions can't import from src/, hence the mirror. Unit tested in
 * src/lib/management-invoice-lines.test.ts.
 *
 * Since 20260904120000 an invoice bills ONE unit, so fee_basis.properties[]
 * holds a single entry and the document is one line naming that apartment. A
 * LEGACY consolidated invoice carries several entries and still itemizes one
 * line per property; a legacy single-property invoice is one line for the
 * whole total.
 *
 * amount_cents is the authoritative GROSS. Each line's NET is split from that
 * property's gross fee; the totals are then reconciled so the displayed NETTO
 * column always foots: net = Σ(line nets), vat = gross − net, gross = amount_cents.
 * (Per-property gross fees are rounded independently in the DB, so summing their
 * splits can differ from splitting the total by a cent — reconciling off the
 * authoritative gross keeps the document internally consistent.)
 */
export type MgmtInvoiceLine = { title: string; netCents: number };

/** Split a gross (incl. VAT) amount into net + VAT at the given whole-percent rate. */
export function splitGrossVat(
  grossCents: number,
  ratePct: number
): { netCents: number; vatCents: number; grossCents: number } {
  const vatCents = Math.round((grossCents * ratePct) / (100 + ratePct));
  return { netCents: grossCents - vatCents, vatCents, grossCents };
}

export type MgmtInvoiceAmounts = {
  lines: MgmtInvoiceLine[];
  netCents: number;
  vatCents: number;
  grossCents: number;
  /** True when the invoice consolidates a per-property breakdown (>1 line). */
  itemized: boolean;
};

/**
 * Resolve an owner management invoice into display line items + reconciled
 * totals. `fallbackTitle` is used for the single line when there's no
 * per-property breakdown.
 */
export function managementInvoiceLines(
  feeBasis: Record<string, unknown> | null | undefined,
  amountCents: number,
  vatRate: number,
  fallbackTitle: string
): MgmtInvoiceAmounts {
  const grossCents = amountCents;
  const raw = (feeBasis ?? {}) as Record<string, unknown>;
  const props = Array.isArray(raw.properties)
    ? (raw.properties as Array<Record<string, unknown>>)
    : [];

  if (props.length > 1) {
    const lines: MgmtInvoiceLine[] = props.map((p) => ({
      title: String(p.apartment_label ?? p.property_title ?? "Objekt"),
      netCents: splitGrossVat(Math.round(Number(p.fee_eur ?? 0) * 100), vatRate)
        .netCents,
    }));
    const netCents = lines.reduce((s, l) => s + l.netCents, 0);
    return {
      lines,
      netCents,
      vatCents: grossCents - netCents,
      grossCents,
      itemized: true,
    };
  }

  // Single line: one per-APARTMENT invoice (the shape every invoice has had
  // since 20260904120000), a legacy per-property invoice, or a consolidated
  // invoice with exactly one property.
  //
  // apartment_label wins over property_title: a per-apartment invoice carries
  // BOTH (its property is set so the payable bank resolves), and naming the
  // building on it would print the same line on every flat in that building.
  const title =
    props.length === 1
      ? String(
          props[0].apartment_label ?? props[0].property_title ?? fallbackTitle
        )
      : fallbackTitle;
  const { netCents } = splitGrossVat(grossCents, vatRate);
  return {
    lines: [{ title, netCents }],
    netCents,
    vatCents: grossCents - netCents,
    grossCents,
    itemized: false,
  };
}

/**
 * COL-483: the number of the invoice a REMAINDER follows, read off the
 * remainder's own row, where it is frozen like every other printed figure.
 * NULL for an invoice that is not a remainder, or whose reference is unknown.
 */
export function managementRemainderOf(inv: {
  remainder_seq?: unknown;
  fee_basis?: unknown;
}): string | null {
  if (!(Number(inv.remainder_seq) > 0)) return null;
  const fb = (inv.fee_basis ?? {}) as Record<string, unknown>;
  const no = fb.remainder_of_invoice_no;
  return typeof no === "string" && no.trim() !== "" ? no : null;
}

/** COL-483: how a remainder names the invoice it follows (the "Bezug" row). */
export function managementRemainderReference(
  previousInvoiceNo: string
): string {
  return `Nachberechnung zu Rechnung ${previousInvoiceNo}`;
}

/**
 * The note a payout confirmation starts with: the ONLY record that the fee was
 * settled against the rent, so it names the invoice and its period rather than
 * leaving an empty box that invites "ok". A remainder names the invoice it
 * follows (COL-483). Stored as the payout's record, so written the same way
 * whatever the admin's language.
 */
export function offsetPayoutNote(row: {
  invoice_no?: string | null;
  period_month?: string | null;
  remainder_seq?: unknown;
  fee_basis?: unknown;
}): string {
  const period = String(row.period_month ?? "").slice(0, 7);
  const previous = managementRemainderOf(row);
  return `Management fee ${row.invoice_no ?? ""}${previous ? ` (remainder of ${previous})` : ""} deducted from the rent collected for ${period}.`.replace(
    /\s+/g,
    " "
  );
}
